import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, Check } from 'lucide-react';
import { useStore } from '../../store/StoreContext';
import { ScreenHeader } from '../../components/ui/ScreenHeader';
import { FormSection } from '../../components/ui/FormSection';
import { NicknameField } from '../../components/integrations/NicknameField';
import { resolveNickname } from '../../lib/mappingNickname';
import { TextField, TextArea } from '../../components/ui/TextField';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Toast } from '../../components/ui/Toast';
import { UpgradePrompt } from '../../components/UpgradePrompt';
import { uid, type Mapping } from '../../types';
import { LimitError } from '../../api/dataApi';
import { starterWebhookSpec } from '../../lib/webhookStarterSpec';

function prettySpec(spec: Record<string, unknown>): string {
  try {
    return JSON.stringify(spec, null, 2);
  } catch {
    return '{}';
  }
}

// Generic-webhook setup (roadmap #9 Phase 1 slice 6, the setup-flow half -
// docs/CLAUDE_ZMIST_INTEGRATION_TOOL_IMPLEMENTATION.md §7.5): name ->
// channel -> starter spec.rules under an Advanced JSON disclosure -> create
// -> payload URL + bearer token -> hand off. Point-and-click authoring of
// spec.rules (a true visual editor) is explicitly NOT this slice - see
// docs/INTEGRATION_TOOL_PHASE0_AUDIT.md §6.6.7's slice table: Advanced JSON
// is the design's own sanctioned fallback (§7.7), not a compromise. Once
// created, the Integration detail shell's read-only three-pane preview
// (MappingVisualPreview.tsx) lets the user immediately check what these
// rules produce against a pasted sample - that's what makes shipping
// "Advanced JSON only" reasonable here rather than a dead end.
export function WebhookSetupScreen() {
  const navigate = useNavigate();
  const { channels, isChannelOwner, addMapping } = useStore();
  const ownedChannels = channels.filter(isChannelOwner);

  const [title, setTitle] = useState('');
  const [nicknameInput, setNicknameInput] = useState('');
  const [channelNickname, setChannelNickname] = useState(ownedChannels[0]?.nickname ?? '');
  const [specText, setSpecText] = useState(() => prettySpec(starterWebhookSpec(ownedChannels[0]?.nickname ?? '')));
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [limitMessage, setLimitMessage] = useState('');
  const [created, setCreated] = useState<Mapping | null>(null);
  const [tokenCopied, setTokenCopied] = useState(false);

  const specError = (() => {
    if (specText.trim() === '') return 'Spec is required.';
    try {
      const parsed = JSON.parse(specText);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        return 'Spec must be a JSON object.';
      }
      return '';
    } catch {
      return 'Spec is not valid JSON.';
    }
  })();

  const onChannelChange = (next: string) => {
    setChannelNickname(next);
    // Keep the starter spec's channel field in sync until the user has
    // opened Advanced and started editing it themselves - once they have,
    // overwriting their edits on every channel-picker click would be a
    // worse surprise than a stale `channel` value they can still see and
    // fix right there in the JSON.
    if (!advancedOpen) setSpecText(prettySpec(starterWebhookSpec(next)));
  };

  const canCreate = title.trim() !== '' && channelNickname.trim() !== '' && specError === '' && !creating;

  const create = async () => {
    if (!canCreate) return;
    setCreating(true);
    setError('');
    try {
      const payload: Mapping = {
        id: uid(),
        nickname: resolveNickname(nicknameInput, '@webhook', uid().slice(0, 8)),
        title: title.trim(),
        description: `Webhook -> #${channelNickname}`,
        isPublic: false,
        sourceType: 'webhook',
        spec: JSON.parse(specText),
      };
      const saved = await addMapping(payload);
      setCreated(saved);
    } catch (err) {
      // {code:"connector_limit"} - a fresh webhook connector past the
      // caller's tier cap (backend/workflows/mapping/create.wsl).
      if (err instanceof LimitError) {
        setLimitMessage(err.message);
      } else {
        setError(err instanceof Error ? err.message : 'Unable to create this integration.');
      }
    } finally {
      setCreating(false);
    }
  };

  const copyToken = async () => {
    if (!created?.ingestToken) return;
    try {
      await navigator.clipboard.writeText(created.ingestToken);
      setTokenCopied(true);
    } catch {
      // Clipboard access denied/unavailable - still on screen to copy by hand.
    }
  };

  if (created) {
    return (
      <div className="flex flex-col gap-4">
        <ScreenHeader title="Connect a webhook" backTo="/integrations/catalog" />

        <FormSection title="Send POSTs here">
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-black/70 dark:text-white/70">Payload URL</span>
            <code className="rounded-lg bg-black/[0.06] dark:bg-white/10 px-3 py-2 text-[13px] font-mono select-all break-all">
              {`POST /ingest/webhook/${created.nickname}`}
            </code>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-black/70 dark:text-white/70">Bearer token</span>
            <p className="text-xs text-black/45 dark:text-white/45">
              Copy it now — it won't be shown again. Send it as{' '}
              <code className="font-mono">Authorization: Bearer &lt;token&gt;</code> on every request; rotating it
              later is possible from the integration's Mapping tab, but there's no way to retrieve this exact value
              again.
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 min-w-0 truncate rounded-lg bg-black/[0.06] dark:bg-white/10 px-3 py-2 text-[13px] font-mono select-all">
                {created.ingestToken}
              </code>
              <Button variant="secondary" onClick={copyToken}>
                {tokenCopied ? <Check size={16} /> : <Copy size={16} />}
                {tokenCopied ? 'Copied' : 'Copy'}
              </Button>
            </div>
          </div>
        </FormSection>

        <FormSection title="Waiting for first event">
          <p className="text-sm text-black/60 dark:text-white/60">
            There's no live delivery status yet (that's a later phase of this roadmap item) - so this won't update on
            its own. Send a real POST, then check the integration's Mapping tab: paste the same payload into its
            preview to see exactly which Signals your <code>rules</code> would produce, and the destination channel
            for the Signals themselves.
          </p>
          <Badge>Configured</Badge>
        </FormSection>

        <Button onClick={() => navigate(`/integrations/${created.id}`)} full>
          Open the visual Mapping editor
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <ScreenHeader title="Connect a webhook" backTo="/integrations/catalog" />

      <FormSection title="Name this integration">
        <TextField
          label="Name"
          placeholder="Order events"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <NicknameField value={nicknameInput} onChange={setNicknameInput} example="@orders" />
      </FormSection>

      <FormSection title="Destination channel">
        {ownedChannels.length === 0 ? (
          <p className="text-sm text-black/60 dark:text-white/60">
            You need to own at least one channel before connecting an integration - create a channel first, then come
            back here.
          </p>
        ) : (
          <Select label="Channel" value={channelNickname} onChange={(e) => onChannelChange(e.target.value)}>
            {ownedChannels.map((c) => (
              <option key={c.id} value={c.nickname}>
                {c.title} ({c.nickname})
              </option>
            ))}
          </Select>
        )}
      </FormSection>

      <FormSection title="What this creates">
        <p className="text-sm text-black/60 dark:text-white/60">
          Each POST body is turned into one or more Signals by the <code>rules</code> below - a starter
          example is filled in. Once created, the integration page opens a visual editor: paste a sample payload,
          click fields to map them, and watch the Signals they produce before you publish.
        </p>
        <button
          type="button"
          onClick={() => setAdvancedOpen((v) => !v)}
          className="self-start text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
        >
          {advancedOpen ? 'Hide' : 'Show'} Advanced (spec JSON)
        </button>
        {advancedOpen && (
          <TextArea
            label="spec (JSON)"
            value={specText}
            onChange={(e) => setSpecText(e.target.value)}
            error={specError || undefined}
            className="font-mono text-[13px] min-h-[200px]"
            spellCheck={false}
          />
        )}
      </FormSection>

      <Button onClick={create} loading={creating} disabled={!canCreate || ownedChannels.length === 0} full>
        Create integration
      </Button>

      {error && <Toast message={error} onDismiss={() => setError('')} />}
      <UpgradePrompt
        open={!!limitMessage}
        onClose={() => setLimitMessage('')}
        message={limitMessage}
        title="Connector limit reached"
      />
    </div>
  );
}
